import type {
  PolicyReportContextBlock,
  InvestigationAlignmentContextBlock,
} from '../policies/policyPacks';
import { buildReportContextForAlias } from '../policies/policyPacks';
import type {
  PolicyReportContextPayload,
  SafetyCaseRecord,
  SafetyDashboardRow,
  SafetyReportArtifactDetailResponse,
  ReliabilityProofPackPreviewResponse,
} from '../../services/reportsApi';

export type InvestigationAlignmentViewModel = {
  scanTemplate: string;
  scanTemplateLabel: string;
  riskFamily: string;
  riskFamilyLabel: string;
  exposureLabel: string;
  occurrenceFamilies: string[];
  causePatterns: string[];
  contributingFactorThemes: string[];
  controlReviewThemes: string[];
  uxSummary: string;
  reportSummary: string;
  claimBoundary: string;
  sourceLabel: string;
};

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asText(value: unknown): string {
  return String(value || '').trim();
}

function asList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
}

function humanizeToken(value: string): string {
  const raw = asText(value);
  if (!raw) return 'Not recorded';
  return raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function riskFamilyLabel(value: string): string {
  const key = asText(value).toLowerCase();
  const labels: Record<string, string> = {
    struck_by_mobile_equipment: 'Struck-by / mobile-equipment interaction',
    restricted_area_access_control: 'Restricted-area access control',
    procedural_control_adherence: 'Procedural-control adherence',
    msi_material_handling: 'MSI / material handling',
    falls_from_elevation: 'Falls from elevation',
    load_handling_interaction: 'Load-handling interaction',
    vehicle_restricted_area_access: 'Vehicle restricted-area access',
    safety_exposure_review: 'Safety exposure review',
  };
  return labels[key] || humanizeToken(key);
}

function normalizePolicyContext(value: unknown): PolicyReportContextPayload | null {
  const raw = asObject(value);
  if (!raw || Object.keys(raw).length === 0) return null;
  return raw as PolicyReportContextPayload;
}

function normalizeAlignment(value: unknown): InvestigationAlignmentContextBlock | null {
  const raw = asObject(value);
  const hasContent = Object.keys(raw).some((key) => {
    const current = raw[key];
    return (Array.isArray(current) && current.length > 0) || asText(current).length > 0;
  });
  return hasContent ? (raw as InvestigationAlignmentContextBlock) : null;
}

function toViewModel(
  policyContext: PolicyReportContextPayload | PolicyReportContextBlock | null | undefined,
): InvestigationAlignmentViewModel | null {
  const context = policyContext || null;
  const alignment = normalizeAlignment(context?.investigation_alignment);
  if (!context || !alignment) return null;
  const controlReviewThemes = asList(context.control_review_focus);
  return {
    scanTemplate: asText(context.scan_template),
    scanTemplateLabel: asText(context.scan_template_label) || humanizeToken(asText(context.scan_template)),
    riskFamily: asText(context.risk_family),
    riskFamilyLabel: riskFamilyLabel(asText(context.risk_family)),
    exposureLabel: asText(context.hazard_exposure_label) || asText(context.short_exposure_label),
    occurrenceFamilies: asList(alignment.occurrence_families),
    causePatterns: asList(alignment.cause_patterns),
    contributingFactorThemes: asList(alignment.contributing_factor_themes),
    controlReviewThemes,
    uxSummary: asText(alignment.ux_summary),
    reportSummary: asText(alignment.report_summary),
    claimBoundary: asText(alignment.claim_boundary),
    sourceLabel: asText(alignment.source_label) || 'Curated WorkSafeBC IIR alignment',
  };
}

function inferAliasFromRecord(record: {
  riskType?: unknown;
  activityType?: unknown;
  riskLabel?: unknown;
  hazardCategory?: unknown;
  unsafeConditionTitle?: unknown;
}): string | null {
  const riskType = asText(record.riskType).toLowerCase();
  const text = [
    record.activityType,
    record.riskLabel,
    record.hazardCategory,
    record.unsafeConditionTitle,
  ]
    .map(asText)
    .join(' ')
    .toLowerCase();
  if (text.includes('material load')) return 'material_load_interaction';
  if (riskType === 'ppe_compliance' || text.includes('ppe')) return 'ppe_compliance';
  if (riskType === 'work_at_height' || text.includes('work at height') || text.includes('fall')) {
    return 'work_at_height';
  }
  if (text.includes('ergonomic') || text.includes('material handling') || text.includes('msi')) {
    return 'ergonomics_material_handling';
  }
  if (riskType === 'zone_intrusion' && (text.includes('forklift') || text.includes('mobile equipment'))) {
    return 'pedestrian_in_forklift_zone';
  }
  if (riskType === 'zone_intrusion') return 'person_in_restricted_area';
  return buildReportContextForAlias(riskType) ? riskType : null;
}

export function resolveInvestigationAlignmentFromPolicyConfig(
  config: Record<string, unknown> | null | undefined,
): InvestigationAlignmentViewModel | null {
  const raw = asObject(config);
  const directContext = normalizePolicyContext(raw.report_context);
  if (directContext?.investigation_alignment) return toViewModel(directContext);
  const alias = asText(asObject(raw.ui_context).policy_alias);
  return toViewModel(alias ? buildReportContextForAlias(alias) : null);
}

export function resolveInvestigationAlignmentFromAlias(
  alias: string | null | undefined,
): InvestigationAlignmentViewModel | null {
  const key = asText(alias);
  return toViewModel(key ? buildReportContextForAlias(key) : null);
}

export function resolveInvestigationAlignmentFromPolicyContext(
  policyContext: PolicyReportContextPayload | PolicyReportContextBlock | null | undefined,
): InvestigationAlignmentViewModel | null {
  return toViewModel(policyContext);
}

export function resolveInvestigationAlignmentFromArtifactDetail(
  detail: SafetyReportArtifactDetailResponse | null | undefined,
): InvestigationAlignmentViewModel | null {
  const candidates = [
    detail?.technical_details?.policy_report_context,
    detail?.technical_details?.report_context,
    detail?.safety_scan_buyer_report?.policy_report_context,
  ];
  for (const candidate of candidates) {
    const resolved = toViewModel(candidate || null);
    if (resolved) return resolved;
  }
  const alias = inferAliasFromRecord({
    riskType: detail?.artifact?.scope?.risk_type,
    activityType: detail?.artifact?.display_labels?.activity_type_label,
    riskLabel: detail?.artifact?.display_labels?.risk_level,
    unsafeConditionTitle: detail?.artifact?.summary,
  });
  return toViewModel(alias ? buildReportContextForAlias(alias) : null);
}

export function resolveInvestigationAlignmentFromProofPreview(
  preview: ReliabilityProofPackPreviewResponse | null | undefined,
): InvestigationAlignmentViewModel | null {
  const context = normalizePolicyContext(
    preview?.evidence_pack?.report_projection?.label_bundle?.policy_report_context,
  );
  return toViewModel(context);
}

export function resolveInvestigationAlignmentFromSafetyCase(
  item: SafetyCaseRecord | SafetyDashboardRow | null | undefined,
): InvestigationAlignmentViewModel | null {
  const scope = asObject((item as SafetyCaseRecord | undefined)?.scope);
  const topLevel = asObject(item);
  const directContext = normalizePolicyContext(
    topLevel.policy_report_context
    || topLevel.report_context
    || scope.policy_report_context
    || scope.report_context,
  );
  if (directContext?.investigation_alignment) return toViewModel(directContext);
  const alias = inferAliasFromRecord({
    riskType: item?.risk_type,
    activityType: (item as SafetyDashboardRow | undefined)?.activity_type,
    riskLabel: item?.risk_label,
    hazardCategory: item?.hazard_category,
    unsafeConditionTitle: (item as SafetyDashboardRow | undefined)?.unsafe_condition_title,
  });
  return toViewModel(alias ? buildReportContextForAlias(alias) : null);
}
